import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    ElementRef,
    inject,
    input,
    OnInit,
    viewChild,
    signal,
    computed,
    ChangeDetectionStrategy,
} from "@angular/core"
import {getRevisionTitle} from "@app/textures/utils/texture-set-revision-utils"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {ThumbnailLayout} from "@common/models/enums/thumbnail-layout"
import {Enums} from "@enums"
import {JobState} from "@generated"
import {Labels} from "@labels"
import {
    ImageTextureSetNodeTextureSetGQL,
    ImageTextureSetNodeTextureSetRevisionGQL,
} from "@material-editor/components/widgets/base-image-texture-set-widget/base-image-texture-set-widget.generated"
import {SocketConnectionsService} from "@socket-editor/services/socket-connections.service"
import tippy from "tippy.js"
import {ImageTextureSetNodeTextureSetRevisionFragment} from "@app/material-editor/components/widgets/base-image-texture-set-widget/base-image-texture-set-widget.generated"
import {MaterialNode} from "@cm/material-nodes"
import {PreviewSceneService} from "@app/material-editor/services/preview-scene.service"
import {fromEvent} from "rxjs"
import {takeUntilDestroyed} from "@angular/core/rxjs-interop"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-base-image-texture-set-widget",
    imports: [],
    templateUrl: "./base-image-texture-set-widget.component.html",
    styleUrl: "./base-image-texture-set-widget.component.scss",
})
export abstract class BaseImageTextureSetWidgetComponent implements OnInit, AfterViewInit {
    public abstract node?: MaterialNode

    public readonly $material = input.required<MaterialNode>({alias: "material"})
    public readonly $materialId = input.required<string>({alias: "materialId"})
    public readonly $materialRevisionId = input<string | undefined>(undefined, {alias: "materialRevisionId"})

    readonly $textureIcon = viewChild<ElementRef<HTMLElement>>("textureIcon")
    readonly $textureInfoTemplate = viewChild<ElementRef<HTMLElement>>("textureInfoTemplate")

    public changeDetectionRef = inject(ChangeDetectorRef)
    public destroyRef = inject(DestroyRef)
    public elementRef = inject(ElementRef)

    readonly imageTextureSetNodeTextureSetRevision = inject(ImageTextureSetNodeTextureSetRevisionGQL)
    readonly imageTextureSetNodeTextureSet = inject(ImageTextureSetNodeTextureSetGQL)
    readonly previewSceneService = inject(PreviewSceneService)
    readonly connections = inject(SocketConnectionsService)

    readonly $textureSetRevisionInfos = signal<{id: string; name?: string | null | undefined}[] | undefined>([])
    readonly $textureSetRevision = signal<ImageTextureSetNodeTextureSetRevisionFragment | undefined>(undefined)
    readonly $textureSetRevisionVersion = signal<number | undefined>(undefined)
    readonly $textureSetLegacyId = signal<number | undefined>(undefined)
    readonly $thumbnailId = signal<string | undefined>(undefined)

    nodeAndTextureSetRevisionSizeParamsEqual: boolean = false

    ngOnInit() {
        void this.fromNodeParameters()

        // don't propagate Delete or Backspace key events to the parent element
        // (they would remove the node)
        fromEvent<KeyboardEvent>(this.elementRef?.nativeElement, "keydown")
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((event) => event.stopPropagation())
    }

    ngAfterViewInit() {
        this.setupTextureInfo()
    }

    abstract fromNodeParameters(): Promise<void>
    abstract updateNodeParameters(applyDefaults: boolean): void
    abstract resetNodeParameters(): void
    abstract resetParameters(): void
    abstract setThumbnail(): void

    async loadTextureSetRevision(revisionId: string | undefined) {
        if (revisionId === this.$textureSetRevision()?.id) {
            return
        }

        if (!revisionId) {
            this.resetParameters()
        } else {
            const {textureSetRevision} = await fetchThrowingErrors(this.imageTextureSetNodeTextureSetRevision)({
                textureSetRevisionId: revisionId,
            })
            this.$textureSetRevision.set(textureSetRevision)
        }
    }

    async loadTextureSet(textureSetLegacyId: number | undefined) {
        if (this.$textureSetLegacyId() === textureSetLegacyId) {
            return
        }
        if (!textureSetLegacyId) {
            this.resetParameters()
            return
        }

        this.$textureSetLegacyId.set(textureSetLegacyId)
        const {textureSet} = await fetchThrowingErrors(this.imageTextureSetNodeTextureSet)({
            textureSetLegacyId,
        })
        this.$textureSetRevisionInfos.set(textureSet.textureSetRevisions ?? [])
    }

    setupTextureInfo() {
        this.changeDetectionRef.detectChanges()
        const textureInfoTemplateValue = this.$textureInfoTemplate()
        if (!textureInfoTemplateValue || !textureInfoTemplateValue.nativeElement) throw Error("textureInfo template not found")
        const textureInfoTemplate = textureInfoTemplateValue.nativeElement
        if (!textureInfoTemplate) {
            return
        }
        const textureIcon = this.$textureIcon()
        if (!textureIcon || !textureIcon.nativeElement) {
            return
        }
        textureInfoTemplate.style.display = "block"
        tippy(textureIcon.nativeElement, {
            content: textureInfoTemplate,
            allowHTML: true,
            placement: "right",
            arrow: false,
            offset: [0, 20],
            interactive: false,
            interactiveBorder: 30,
            trigger: "mouseenter",
            theme: "light",
            animation: "scale-subtle",
            delay: [200, 0],
            appendTo: (reference: Element) => (reference.closest(".cdk-overlay-popover") as HTMLElement) ?? document.body,
        })
    }

    // FIXME: this is a quick hack to update the material graph after changing the texture revision.
    triggerGraphUpdate(): void {
        this.previewSceneService.$refresh.next()
    }

    setTextureSetRevisionVersion(revisionId: string | undefined) {
        const infos = this.$textureSetRevisionInfos()
        this.$textureSetRevisionVersion.set(revisionId && infos ? infos.length - infos.findIndex((revision) => revision.id === revisionId) : undefined)
    }

    readonly $isProcessing = computed(
        () => !!this.$textureSetRevision()?.editsProcessingJob && this.$textureSetRevision()?.editsProcessingJob?.state !== JobState.Complete,
    )

    get partialImageResource() {
        const revision = this.$textureSetRevision()
        return revision
            ? {
                  id: revision.id,
                  width: revision.width,
                  height: revision.height,
                  displacement: revision.displacement ?? undefined,
                  mapAssignments: revision.mapAssignments.map((assignment) => ({
                      textureType: assignment.textureType,
                      dataObjectLegacyId: assignment.dataObject.legacyId,
                  })),
              }
            : {}
    }

    protected readonly Enums = Enums
    protected readonly Labels = Labels
    protected readonly ThumbnailLayout = ThumbnailLayout
    protected readonly getRevisionTitle = getRevisionTitle
}
